import { defineComponent, computed } from 'vue';
import __vue_component__ from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import { ButtonIconAnimation } from '../../buttons/ButtonComponent/model.js';
import __vue_component__$1 from '../FixedFooterButton.vue.js';
import { useSharedNavBarLogic } from '../NavBar/helper.js';
import __vue_component__$3 from '../../specific/portal/components/Portal/components/SearchResults/components/PortalMapViewToggle.vue.js';
import TransitionExpand from '../../transitions/TransitionExpand/TransitionExpand.js';
import __vue_component__$2 from '../../ui/ProgressSteps/ProgressSteps.vue.js';
import __vue_component__$4 from '../../ui/Skeleton.vue.js';
import { useBrowserWidth } from '../../../composables/browser.js';
import '@caresend/utils';
import 'lodash.isequal';
import { getRoute } from '../../../router/helpers/initRouter.js';
import '@caresend/types';
import '@sentry/vue';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import 'immutability-helper';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import '../../../router/routes/dashboard/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/visitsPage/routes.js';
import { isExactTheme } from '../../../store/modules/app/helpers.js';

var script = defineComponent({
    name: 'Header',
    components: {
        ButtonComponent: __vue_component__,
        FixedFooterButton: __vue_component__$1,
        ProgressSteps: __vue_component__$2,
        PortalMapViewToggle: __vue_component__$3,
        Skeleton: __vue_component__$4,
        TransitionExpand,
    },
    props: {
        headingValues: {
            type: Object,
            required: true,
        },
    },
    setup(props) {
        const { isMobile } = useBrowserWidth();
        const route = getRoute();
        const { isLoggedIn } = useSharedNavBarLogic();
        /**
         * This is to handle the limbo state during navigation
         * which occurs when going to a route which has auth required
         */
        const titleLoading = computed(() => { var _a, _b; return (_b = (((_a = route.value.meta) === null || _a === void 0 ? void 0 : _a.authRequired) && !isLoggedIn.value)) !== null && _b !== void 0 ? _b : false; });
        const buttonSize = computed(() => {
            if (isExactTheme())
                return 'md';
            return isMobile.value ? 'sm' : 'md';
        });
        const headerHasNoNonConfirmContent = computed(() => {
            const { backComponent, backLocation, progressSteps, secondaryComponent, tertiaryComponent, title, titleComponent, } = props.headingValues;
            const headerHasNoContent = (!title
                && !titleComponent
                && !backLocation
                && !backComponent
                && !progressSteps
                && !secondaryComponent
                && !tertiaryComponent);
            return headerHasNoContent;
        });
        const headerHasTitle = computed(() => !!(titleLoading.value
            || props.headingValues.progressSteps
            || props.headingValues.titleComponent
            || props.headingValues.title));
        const footerOnlyMobile = computed(() => !!props.headingValues.mobileFooter && headerHasNoNonConfirmContent.value);
        const footerOnlyGlobal = computed(() => !!props.headingValues.globalFooter && headerHasNoNonConfirmContent.value);
        const classNames = computed(() => {
            const { backLocation, confirmEvent, disableStickyHeader, globalFooter, mobileFooter, leftAlignTitle, overflowVisible, } = props.headingValues;
            const names = [];
            if (backLocation)
                names.push('Header--with-back-button');
            if (confirmEvent)
                names.push('Header--with-confirm-button');
            if (disableStickyHeader)
                names.push('Header--disable-sticky-header');
            if (globalFooter)
                names.push('Header--with-confirm-global-footer');
            if (mobileFooter)
                names.push('Header--with-confirm-mobile-footer');
            if (leftAlignTitle)
                names.push('Header--left-align-title');
            if (footerOnlyGlobal.value)
                names.push('Header--footer-only-global');
            if (footerOnlyMobile.value)
                names.push('Header--footer-only-mobile');
            if (!overflowVisible)
                names.push('Header--overflow-hidden');
            switch (props.headingValues.confirmIconActiveAnimation) {
                case 'rotate':
                    names.push('Header--with-confirm-icon-rotate');
                    break;
            }
            return names;
        });
        const showConfirmButton = computed(() => {
            const { confirmEvent, confirmTo, confirmDisabled, } = props.headingValues;
            return !!confirmEvent
                || !!confirmTo
                || !!(confirmDisabled === null || confirmDisabled === void 0 ? void 0 : confirmDisabled.value);
        });
        const showSecondaryComponent = computed(() => !!props.headingValues.secondaryComponent);
        /**
         * Use no-op event if no `confirmEvent` is available to satisfy
         * template type checking. (If only navigation is required, use `confirmTo`
         * rather than `confirmEvent`)
         */
        const computedConfirmEvent = computed(() => { var _a; return (_a = props.headingValues.confirmEvent) !== null && _a !== void 0 ? _a : (() => { }); });
        const computedConfirmTo = computed(() => {
            var _a;
            if (props.headingValues.confirmEvent)
                return null;
            return (_a = props.headingValues.confirmTo) !== null && _a !== void 0 ? _a : null;
        });
        const confirmDisabled = computed(() => { var _a; return !!((_a = props.headingValues.confirmDisabled) === null || _a === void 0 ? void 0 : _a.value); });
        const confirmIconAnimation = computed(() => props.headingValues.confirmIcon === 'arrow-right'
            ? ButtonIconAnimation.RIGHT
            : undefined);
        const progressSteps = computed(() => { var _a; return (_a = props.headingValues.progressSteps) === null || _a === void 0 ? void 0 : _a.value; });
        const headerRightTitlelessClass = computed(() => props.headingValues.title ? '' : 'Header__right--titleless');
        return {
            buttonSize,
            classNames,
            computedConfirmEvent,
            computedConfirmTo,
            confirmDisabled,
            confirmIconAnimation,
            headerRightTitlelessClass,
            headerHasTitle,
            isMobile,
            progressSteps,
            showConfirmButton,
            showSecondaryComponent,
            titleLoading,
        };
    },
});

export { script as default };
